import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import SelectSearch from 'react-select-search';
import Fuse from 'fuse.js';

import { ReactComponent as CaretDownIcon } from 'assets/icon-angle-down.svg';
import { ReactComponent as CaretUpIcon } from 'assets/icon-angle-up.svg';
import { ReactComponent as SearchIcon } from 'assets/icon-search.svg';
import { ReactComponent as ClearIcon } from 'assets/icon-close-round.svg';

import { DropdownWrapper, ListColor } from './styles';

const DropdownSearch = ({
  id,
  className,
  options,
  placeholder,
  value,
  error,
  onBlur,
  onChange,
  isColor,
  isDisabled,
  color,
  titleNotFound,
  search,
  isAdvisor,
  selectedAdvisor,
}) => {
  const { t } = useTranslation();
  const theme = useSelector((state) => state.theme);
  const { mainColor } = theme;
  const [focused, setFocused] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef(null);

  const onClearInput = () => {
    onChange('');
    const findDocumentId = document.getElementById(`${id}`);
    const targetInput = findDocumentId.querySelector('.select-search__input');
    targetInput.focus();
    setIsOpen(!isOpen);
    setFocused(!focused);
  };

  const onCaretDown = () => {
    const findDocumentId = document.getElementById(`${id}`);
    const targetInput = findDocumentId.querySelector('.select-search__input');
    targetInput.focus();
    setIsOpen(!isOpen);
    setFocused(!focused);
  };

  const onBlurInternal = () => {
    setFocused(false);
    setIsOpen(false);
    if (onBlur) {
      onBlur();
    }
  };

  const renderStateColor = (props, option, snapshot, classNamess) => {
    return (
      <button {...props} className={classNamess} type="button">
        <span>
          {isColor && (
            <div className="d-flex">
              <ListColor color={option.color} />
              <span>{option.name}</span>
            </div>
          )}
          {isAdvisor && (
            <div className="d-flex align-items-center">
              <div className="circle">
                <span className="circle-text text-uppercase">{option.name.substring(0, 1)}</span>
              </div>
              <span className="ml-1">{option.name}</span>
            </div>
          )}
          {!isColor && !isAdvisor && <span>{option.name}</span>}
        </span>
      </button>
    );
  };

  return (
    <DropdownWrapper
      className={classNames(
        {
          error,
          'has-focus': focused,
        },
        className
      )}
      selectedColor={color}
      selectedAdvisor={selectedAdvisor}
      isColor={isColor}
      isAdvisor={isAdvisor}
      isDisabled={isDisabled}
      mainColor={mainColor}
    >
      {isColor && color && !focused && <span className="selected-color" />}
      {isAdvisor && selectedAdvisor && !focused && (
        <div className="selected-advisor">{selectedAdvisor}</div>
      )}
      <SelectSearch
        id={id}
        printOptions={isOpen ? 'always' : 'on-focus'}
        renderOption={renderStateColor}
        ref={inputRef}
        options={options}
        placeholder={placeholder === '' ? t('general_search') : placeholder}
        value={value}
        search={search}
        filterOptions={(optis) => {
          const fuse = new Fuse(optis, {
            keys: ['name', 'value'],
            threshold: 0.3,
          });

          return (val) => {
            if (!val.length) {
              return optis;
            }
            return fuse.search(val);
          };
        }}
        emptyMessage={titleNotFound === '' ? t('general_not_found') : titleNotFound}
        onFocus={() => setFocused(true)}
        onBlur={onBlurInternal}
        onChange={onChange}
        disabled={isDisabled}
      />
      <div className="icons-wrapper">
        {value && !focused && (
          <div aria-hidden="true" onClick={() => onClearInput()}>
            <ClearIcon className="clear-icon" />
          </div>
        )}
        {search && (
          <>
            {!focused ? (
              <div aria-hidden="true" onClick={() => onCaretDown()}>
                <SearchIcon className="search-icon" />
              </div>
            ) : (
              <SearchIcon className="search-icon" onClick={onBlurInternal} />
            )}
          </>
        )}
        {!focused && (
          <div aria-hidden="true" onClick={() => onCaretDown()}>
            <CaretDownIcon className="caret-down-icon ml-1" />
          </div>
        )}
        {focused && <CaretUpIcon className="caret-up-icon ml-1" onClick={onBlurInternal} />}
      </div>
    </DropdownWrapper>
  );
};

DropdownSearch.defaultProps = {
  className: '',
  placeholder: '',
  value: '',
  error: false,
  onBlur: undefined,
  onChange: () => null,
  isColor: false,
  isDisabled: false,
  color: '',
  titleNotFound: '',
  search: true,
  isAdvisor: false,
  selectedAdvisor: '',
};

DropdownSearch.propTypes = {
  id: PropTypes.string.isRequired,
  className: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })
  ).isRequired,
  placeholder: PropTypes.string,
  value: PropTypes.string,
  error: PropTypes.bool,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  isColor: PropTypes.bool,
  isDisabled: PropTypes.bool,
  color: PropTypes.string,
  titleNotFound: PropTypes.string,
  search: PropTypes.bool,
  isAdvisor: PropTypes.bool,
  selectedAdvisor: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
};

export default DropdownSearch;
